import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

function renderAnswer(text) {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const blocks = [];
  let list = [];
  const flush = () => { if (list.length) { blocks.push({ type: "ul", items: [...list] }); list = []; } };
  lines.forEach((l) => {
    if (l.startsWith("- ")) list.push(l.slice(2));
    else { flush(); blocks.push({ type: "p", text: l }); }
  });
  flush();
  return blocks;
}

function Answer({ text }) {
  const blocks = renderAnswer(text);
  return (
    <div className="space-y-3 text-muted-foreground leading-relaxed pr-4">
      {blocks.map((b, i) =>
        b.type === "ul" ? (
          <ul key={i} className="list-disc pl-5 space-y-1.5">
            {b.items.map((li, j) => <li key={j}>{li}</li>)}
          </ul>
        ) : (
          <p key={i}>{b.text}</p>
        )
      )}
    </div>
  );
}

export default function FAQ() {
  const { lang } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data.faqs)).finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = faqs.filter((item) =>
    !q || (item[`question_${lang}`] || "").toLowerCase().includes(q) || (item[`answer_${lang}`] || "").toLowerCase().includes(q)
  );

  return (
    <div data-testid="faq-page" className="mx-auto max-w-3xl px-5 lg:px-10 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center mb-10">
        <p className="overline text-gold mb-4">{lang === "es" ? "Ayuda" : "Help"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light">
          {lang === "es" ? "Preguntas Frecuentes (FAQs)" : "Frequently Asked Questions (FAQs)"}
        </h1>
        <p className="mt-6 text-muted-foreground leading-relaxed">
          {lang === "es"
            ? "A continuación encontrarás respuestas a las preguntas que nuestros clientes realizan con mayor frecuencia. Si necesitas ayuda adicional, nuestro equipo de atención al cliente estará encantado de asistirte."
            : "Below you'll find answers to the questions our customers ask most frequently. If you need additional help, our customer service team will be delighted to assist you."}
        </p>
      </motion.div>

      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={lang === "es" ? "Busca respuestas…" : "Search answers…"}
          className="rounded-none h-12 pl-12 pr-10 text-base"
          data-testid="faq-search-input"
        />
        {query && (
          <button onClick={() => setQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xl leading-none" data-testid="faq-search-clear" aria-label="Clear">×</button>
        )}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" data-testid="faq-no-results">
          <p className="font-serif text-2xl mb-2">{lang === "es" ? "Sin resultados" : "No results found"}</p>
          <p className="text-muted-foreground">{lang === "es" ? "Prueba con otro término de búsqueda." : "Try a different search term."}</p>
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {filtered.map((item, i) => (
            <AccordionItem key={item.id} value={`faq-${item.id}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-serif text-lg py-5 text-left" data-testid={`faq-trigger-${i}`}>
                {item[`question_${lang}`]}
              </AccordionTrigger>
              <AccordionContent>
                <Answer text={item[`answer_${lang}`]} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <div className="text-center mt-14">
        <p className="text-muted-foreground mb-5">
          {lang === "es" ? "¿No encuentras lo que buscas?" : "Can't find what you're looking for?"}
        </p>
        <Link to="/contact">
          <Button className="rounded-none h-12 px-8" data-testid="faq-contact-btn">
            {lang === "es" ? "Contáctanos" : "Contact Us"}
          </Button>
        </Link>
      </div>
    </div>
  );
}
